import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TiltProductCard = ({ product, index = 0 }) => {
  const ref = useRef(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const tagline = t(`products.${product.slug}.tagline`);
  const short = t(`products.${product.slug}.short`);

  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 200, damping: 20 });
  const sy = useSpring(my, { stiffness: 200, damping: 20 });
  const rotX = useTransform(sy, [0, 1], [6, -6]);
  const rotY = useTransform(sx, [0, 1], [-6, 6]);

  const Icon = product.icon;

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    mx.set(px);
    my.set(py);
    el.style.setProperty("--mx", `${px * 100}%`);
    el.style.setProperty("--my", `${py * 100}%`);
  };

  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
    if (ref.current) {
      ref.current.style.setProperty("--mx", "50%");
      ref.current.style.setProperty("--my", "50%");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.2, 0.7, 0.2, 1] }}
      style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="product-card p-8 md:p-10 will-change-transform"
      data-acc={product.accent}
      data-testid={`product-card-${product.slug}`}
    >
      <div style={{ "--acc": product.accentColor.replace("hsl(", "").replace(")", "") }}>
        <div className="flex items-start justify-between gap-4">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-2xl"
            style={{
              background: `linear-gradient(135deg, ${product.accentColor}, transparent)`,
              boxShadow: `inset 0 0 0 1px ${product.accentColor}55, 0 20px 50px -20px ${product.accentGlow}`,
            }}
          >
            <Icon className="h-7 w-7 text-white" strokeWidth={1.6} />
          </div>
          {product.status === "coming_soon" && (
            <span
              className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{ background: `${product.accentColor}22`, color: product.accentColor }}
              data-testid={`coming-soon-${product.slug}`}
            >
              {t("common.comingSoon")}
            </span>
          )}
        </div>

        <h3 className="font-display mt-8 text-2xl md:text-3xl text-foreground">{product.name}</h3>
        <p className="mt-2 text-sm font-medium" style={{ color: product.accentColor }}>
          {tagline}
        </p>
        <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-muted-foreground">
          {short}
        </p>

        <Link
          to={`/${product.slug}`}
          data-testid={`product-learn-more-${product.slug}`}
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold group"
          style={{ color: product.accentColor }}
        >
          {t("solutions.learnMore")}
          <ArrowUpRight
            className={`h-4 w-4 transition-transform group-hover:${isRTL ? "-translate-x-1" : "translate-x-1"} group-hover:-translate-y-1`}
          />
        </Link>
      </div>
    </motion.div>
  );
};

export default TiltProductCard;
